import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import api, { assetUrl } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, Bar, BarChart, CartesianGrid, PieChart, Pie, Cell } from 'recharts';
import { toast } from 'sonner';
import { DollarSign, ShoppingBag, Users, Palette, RefreshCw, Sparkles, Upload, FileText, Mail, Trash2, Archive, ArchiveRestore, LayoutDashboard, Settings as SettingsIcon, Bell, TrendingUp, UserCog, Search, Shield, Receipt, FileDown, Edit3, Plus, Package, Send, Reply, X, Flag, ShieldCheck, ShieldAlert, ShieldX, Crown, Award, Gift, Minus, Power, PowerOff } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import RichEditor from '@/components/RichEditor';
import { useCurrency } from '@/context/CurrencyContext';
import { useConfirm } from '@/context/ConfirmContext';
import Pagination from '@/components/Pagination';
import BulkActionBar from '@/components/BulkActionBar';
import { useSelection } from '@/lib/useSelection';
import { openInvoice } from '@/lib/invoice';
import { Kpi } from './_shared';

const ProductImageUpload = ({ value, onChange }) => {
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);

  const uploadFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please choose an image file'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('Max size is 10MB'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/upload/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange(data.url);
      toast.success('Image uploaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setDragOver(false);
    const file = e.dataTransfer?.files?.[0]; if (file) uploadFile(file);
  };
  const preview = value ? (value.startsWith('http') ? value : assetUrl(value)) : '';

  return (
    <div className="mt-1 space-y-2">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`relative rounded-xl border-2 border-dashed transition-colors flex flex-col items-center justify-center text-center px-3 py-6 cursor-pointer ${dragOver ? 'border-orange-400 bg-orange-500/10' : 'border-white/15 hover:border-white/25'}`}
        style={{ background: dragOver ? undefined : 'var(--jw-bg-elev)' }}
        onClick={() => document.getElementById('admin-product-image-file')?.click()}
        data-testid="admin-product-image-dropzone"
      >
        {preview ? (
          <img src={preview} alt="preview" className="max-h-40 object-contain rounded-md" />
        ) : (
          <>
            <Upload className="w-6 h-6 mb-2" style={{ color: 'var(--jw-text-muted)' }} />
            <p className="text-sm">{uploading ? 'Uploading...' : 'Drag & drop an image here, or click to browse'}</p>
            <p className="text-[11px]" style={{ color: 'var(--jw-text-subtle)' }}>PNG, JPG, WEBP up to 10MB</p>
          </>
        )}
        <input
          id="admin-product-image-file"
          type="file"
          accept="image/*"
          className="hidden"
          data-testid="admin-product-image-file-input"
          onChange={(e) => uploadFile(e.target.files?.[0])}
        />
      </div>
      <div className="flex items-center gap-2">
        <Input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="…or paste an image URL"
          data-testid="admin-product-image-input"
          className="text-xs"
        />
        {value && (
          <Button type="button" variant="ghost" size="sm" onClick={() => onChange('')} className="text-rose-400 h-9">Clear</Button>
        )}
      </div>
    </div>
  );
};

const CATEGORY_LABELS = { phone_case: 'Phone Case', sticker: 'Sticker', label: 'Label' };
const KIND_LABELS = { model: 'Model / size', variant: 'Finish / variant' };

// Multi-select using the catalog options for a given (category, kind).
// value/onChange operate on comma-separated strings so this drops into the
// existing product-edit form fields without any schema change.
const CatalogPicker = ({ category, kind, value, onChange, testId }) => {
  const [options, setOptions] = useState([]);
  useEffect(() => {
    if (!category) { setOptions([]); return; }
    api.get('/catalog-options', { params: { category, kind } })
      .then(({ data }) => setOptions(data || []))
      .catch(() => setOptions([]));
  }, [category, kind]);
  const selected = new Set(String(value || '').split(',').map(s => s.trim()).filter(Boolean));
  const toggle = (label) => {
    const next = new Set(selected);
    if (next.has(label)) next.delete(label); else next.add(label);
    onChange(Array.from(next).join(', '));
  };
  return (
    <div data-testid={testId}>
      <div className="flex flex-wrap gap-2 mt-1">
        {options.length === 0 && (
          <span className="text-xs" style={{ color: 'var(--jw-text-subtle)' }}>
            No {kind === 'model' ? 'models/sizes' : 'finishes'} defined for this category yet. Add some in Admin → Catalog.
          </span>
        )}
        {options.map((o) => (
          <button
            key={o.id}
            type="button"
            onClick={() => toggle(o.label)}
            data-testid={`${testId}-option-${o.label.replace(/\s+/g, '-').toLowerCase()}`}
            data-selected={selected.has(o.label) ? 'true' : 'false'}
            className="jw-chip"
          >{o.label}</button>
        ))}
      </div>
      {selected.size > 0 && (
        <p className="text-[11px] mt-2" style={{ color: 'var(--jw-text-subtle)' }}>
          Selected: {Array.from(selected).join(', ')}
        </p>
      )}
    </div>
  );
};

export const CatalogSection = () => {
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState('phone_case');
  const [kind, setKind] = useState('model');
  const [label, setLabel] = useState('');
  const [sortOrder, setSortOrder] = useState(0);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    api.get('/admin/catalog-options').then(({ data }) => setItems(data || []));
  };
  useEffect(() => { load(); }, []);

  const filtered = items.filter((o) => o.category === category && o.kind === kind);

  const submit = async () => {
    if (!label.trim()) { toast.error('Label is required'); return; }
    setLoading(true);
    try {
      if (editing) {
        await api.patch(`/admin/catalog-options/${editing.id}`, { category, kind, label: label.trim(), sort_order: Number(sortOrder) || 0 });
        toast.success('Updated');
      } else {
        await api.post('/admin/catalog-options', { category, kind, label: label.trim(), sort_order: Number(sortOrder) || 0 });
        toast.success('Added');
      }
      setLabel(''); setSortOrder(0); setEditing(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally { setLoading(false); }
  };
  const remove = async (o) => {
    if (!window.confirm(`Delete "${o.label}"? This won't remove it from products already using it.`)) return;
    await api.delete(`/admin/catalog-options/${o.id}`);
    toast.success('Deleted'); load();
  };
  const startEdit = (o) => { setEditing(o); setCategory(o.category); setKind(o.kind); setLabel(o.label); setSortOrder(o.sort_order || 0); };

  return (
    <div className="space-y-6" data-testid="admin-catalog-section">
      <div>
        <h1 className="font-display text-2xl font-bold">Catalog Options</h1>
        <p className="text-sm mt-1" style={{ color: 'var(--jw-text-muted)' }}>
          Master list of iPhone models, sticker sizes, label shapes and finishes. Values here appear as multi-select chips inside every product edit form.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {['phone_case', 'sticker', 'label'].map((c) => (
          <button key={c} onClick={() => setCategory(c)} data-selected={category === c ? 'true' : 'false'} data-testid={`admin-catalog-cat-${c}-button`} className="jw-chip">{CATEGORY_LABELS[c]}</button>
        ))}
        <span className="mx-2 opacity-40">|</span>
        {['model', 'variant'].map((k) => (
          <button key={k} onClick={() => setKind(k)} data-selected={kind === k ? 'true' : 'false'} data-testid={`admin-catalog-kind-${k}-button`} className="jw-chip">{KIND_LABELS[k]}</button>
        ))}
      </div>

      <Card className="jw-card p-5 space-y-3">
        <h3 className="font-semibold text-sm">{editing ? 'Edit option' : 'Add new option'}</h3>
        <div className="grid sm:grid-cols-3 gap-3">
          <div><label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>Label</label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. iPhone 16 Pro" data-testid="admin-catalog-label-input" />
          </div>
          <div><label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>Sort order</label>
            <Input type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} data-testid="admin-catalog-sort-input" />
          </div>
          <div className="flex items-end gap-2">
            <Button onClick={submit} disabled={loading} data-testid="admin-catalog-save-button" className="jw-btn-primary">{editing ? 'Save changes' : 'Add option'}</Button>
            {editing && <Button variant="ghost" onClick={() => { setEditing(null); setLabel(''); setSortOrder(0); }}>Cancel</Button>}
          </div>
        </div>
        <p className="text-[11px]" style={{ color: 'var(--jw-text-subtle)' }}>Category: {CATEGORY_LABELS[category]} · Kind: {KIND_LABELS[kind]}</p>
      </Card>

      <Card className="jw-card p-0 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Label</TableHead>
              <TableHead>Sort</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={3} className="text-center py-8" style={{ color: 'var(--jw-text-muted)' }}>No options yet.</TableCell></TableRow>
            )}
            {filtered.map((o) => (
              <TableRow key={o.id} data-testid={`admin-catalog-row-${o.id}`}>
                <TableCell className="font-medium">{o.label}</TableCell>
                <TableCell>{o.sort_order}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => startEdit(o)} data-testid={`admin-catalog-edit-${o.id}-button`}>Edit</Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(o)} data-testid={`admin-catalog-delete-${o.id}-button`} className="text-red-400">Delete</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};

export const ProductsSection = () => {
  const { format } = useCurrency();
  const confirm = useConfirm();
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  // Live catalog-sourced models keyed by type, used when the product has an
  // empty `models` array (unified SKUs) so the admin can still set per-model
  // prices against the catalog labels without freezing them onto the product.
  const [catalogModels, setCatalogModels] = useState([]);

  const load = () => api.get('/products').then(({ data }) => setProducts(data));
  useEffect(() => { load(); }, []);

  // Fetch catalog-sourced models for the currently-editing product type.
  useEffect(() => {
    if (!editing?.type) { setCatalogModels([]); return; }
    api.get('/catalog-options', { params: { category: editing.type, kind: 'model' } })
      .then(({ data }) => setCatalogModels((data || []).map((o) => o.label)))
      .catch(() => setCatalogModels([]));
  }, [editing?.type]);

  const newProduct = () => { setEditing({ id: '', name: '', type: 'sticker', price: 3.99, variants: '', models: '', model_pricing: {}, image: '', description: '' }); setOpen(true); };
  const editProduct = async (p) => {
    // Load the RAW product doc (no catalog-enrichment) so we edit whatever the
    // admin explicitly set — unified SKUs have empty models/variants arrays and
    // must stay empty to keep sourcing options live from catalog_options.
    try {
      const { data } = await api.get(`/admin/products/${p.id}`);
      setEditing({
        ...data,
        variants: (data.variants || []).join(', '),
        models: (data.models || []).join(', '),
        model_pricing: data.model_pricing || {},
      });
    } catch {
      // Fallback to the enriched card doc so the admin can still edit.
      setEditing({ ...p, variants: (p.variants || []).join(', '), models: (p.models || []).join(', '), model_pricing: p.model_pricing || {} });
    }
    setOpen(true);
  };
  const save = async () => {
    const ownModels = editing.models.split(',').map(s => s.trim()).filter(Boolean);
    // Effective model set = the product's own list if set, else the live catalog
    // labels (unified SKUs). Prices for models outside this set are dropped so
    // stale keys don't sneak back in via variant first-segment parsing.
    const effectiveModels = ownModels.length ? ownModels : catalogModels;
    const mp = {};
    Object.entries(editing.model_pricing || {}).forEach(([k, v]) => {
      if (!effectiveModels.includes(k)) return;
      const n = Number(v);
      if (Number.isFinite(n) && v !== '' && v !== null && n >= 0) mp[k] = n;
    });
    const payload = {
      id: editing.id || undefined,
      name: editing.name, type: editing.type, price: Number(editing.price),
      variants: editing.variants.split(',').map(s => s.trim()).filter(Boolean),
      models: ownModels,
      model_pricing: mp,
      image: editing.image, description: editing.description,
    };
    try {
      if (editing.id && products.find(p => p.id === editing.id)) {
        await api.patch(`/admin/products/${editing.id}`, payload);
      } else {
        await api.post('/admin/products', payload);
      }
      toast.success('Product saved');
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const del = async (id) => {
    const ok = await confirm({ title: 'Delete product?', description: 'This cannot be undone.', confirmLabel: 'Delete', variant: 'danger' });
    if (!ok) return;
    try { await api.delete(`/admin/products/${id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-lg">Products</h3>
          <p className="text-sm" style={{ color: 'var(--jw-text-muted)' }}>Add or edit the products customers can buy.</p>
        </div>
        <Button onClick={newProduct} data-testid="admin-product-new-button" className="jw-btn-glossy"><Plus className="w-4 h-4 mr-1" /> New product</Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {products.map(p => (
          <Card key={p.id} className="jw-card overflow-hidden" data-testid={`admin-product-${p.id}-card`}>
            <div className="aspect-video overflow-hidden" style={{ background: 'var(--jw-bg-elev)' }}>
              <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
            </div>
            <div className="p-4">
              <div className="flex items-baseline justify-between">
                <h4 className="font-semibold">{p.name}</h4>
                <span className="text-sm">{format(p.price)}</span>
              </div>
              <p className="text-xs capitalize mt-0.5" style={{ color: 'var(--jw-text-subtle)' }}>{p.type.replace('_', ' ')}</p>
              <div className="flex gap-2 mt-3">
                <Button size="sm" onClick={() => editProduct(p)} data-testid={`admin-product-${p.id}-edit-button`} className="jw-btn-gloss-ghost h-8"><Edit3 className="w-4 h-4 mr-1" /> Edit</Button>
                <Button size="icon" variant="ghost" onClick={() => del(p.id)} data-testid={`admin-product-${p.id}-delete-button`} className="text-rose-400 h-8 w-8"><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="admin-product-dialog" className="max-w-lg">
          {editing && (
            <>
              <DialogHeader><DialogTitle>{editing.id && products.find(p => p.id === editing.id) ? 'Edit product' : 'New product'}</DialogTitle></DialogHeader>
              <div className="space-y-3 max-h-[60vh] overflow-auto jw-scrollbar pr-1">
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2"><label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>Name</label>
                    <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="admin-product-name-input" /></div>
                  <div><label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>Type</label>
                    <Select value={editing.type} onValueChange={(v) => setEditing({ ...editing, type: v })}>
                      <SelectTrigger data-testid="admin-product-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="phone_case">Phone Case</SelectItem><SelectItem value="sticker">Sticker</SelectItem><SelectItem value="label">Label</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div><label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>Price (USD)</label>
                    <Input type="number" step="0.01" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} data-testid="admin-product-price-input" /></div>
                </div>
                <div>
                  <label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>Product image</label>
                  <ProductImageUpload
                    value={editing.image}
                    onChange={(url) => setEditing({ ...editing, image: url })}
                  />
                </div>
                <div><label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>Finishes / variants</label>
                  <CatalogPicker category={editing.type} kind="variant" value={editing.variants} onChange={(v) => setEditing({ ...editing, variants: v })} testId="admin-product-variants" /></div>
                <div><label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>Models / sizes</label>
                  <CatalogPicker category={editing.type} kind="model" value={editing.models} onChange={(v) => setEditing({ ...editing, models: v })} testId="admin-product-models" /></div>

                {(() => {
                  const own = editing.models.split(',').map(s => s.trim()).filter(Boolean);
                  const effective = own.length ? own : catalogModels;
                  if (effective.length === 0) return null;
                  return (
                    <div data-testid="admin-product-model-pricing-grid">
                      <label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>
                        Per-model price (USD) — optional, blank uses base price
                        {own.length === 0 && catalogModels.length > 0 && (
                          <span className="ml-1" style={{ color: 'var(--jw-text-subtle)' }}>
                            (sourced from catalog)
                          </span>
                        )}
                      </label>
                      <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {effective.map((m) => (
                          <div key={m} className="flex items-center gap-2 jw-panel rounded-md px-2 py-1.5">
                            <span className="text-xs flex-1 truncate" title={m}>{m}</span>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={(editing.model_pricing || {})[m] ?? ''}
                              placeholder={String(editing.price ?? '')}
                              data-testid={`admin-product-model-price-${m.replace(/\s+/g, '-').toLowerCase()}-input`}
                              className="w-24 h-8 text-right"
                              onChange={(e) => setEditing({
                                ...editing,
                                model_pricing: { ...(editing.model_pricing || {}), [m]: e.target.value },
                              })}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
                <div><label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>Description</label>
                  <Textarea rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="admin-product-description-input" /></div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={save} data-testid="admin-product-save-button" className="jw-btn-glossy">Save</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductsSection;
